import type { EntryKind, IconView, Kind } from './types.ts';

export const KIND_NAME: Record<Kind, string> = { skill: '做法', plugin: '整包' };
/** What an entry is to the person, as the administrator's pages say it. */
export const ENTRY_KIND_NAME: Record<EntryKind, string> = { skill: '技能', plugin: '插件', prompt: '提示词模板' };

export const esc = (value: unknown): string =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] as string);

export const $ = <T extends HTMLElement = HTMLElement>(selector: string, root: ParentNode = document): T | null => root.querySelector<T>(selector);
export const must = <T extends HTMLElement = HTMLElement>(selector: string, root: ParentNode = document): T => {
  const found = root.querySelector<T>(selector);
  if (!found) throw new Error(`The page has no ${selector}.`);
  return found;
};

export const fmtTime = (ms: number) => new Date(ms).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
export const fmtDate = (iso: string | number) => new Date(iso).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
export const fmtSize = (bytes: number) => (bytes < 1024 ? `${bytes} B` : bytes < 1_048_576 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1_048_576).toFixed(1)} MB`);

/** A short piece of Markdown as a page shows it: headings, lists, code, emphasis. Everything is escaped first. */
export function md(source: string): string {
  const fence = '`'.repeat(3);
  let out = '';
  let list: { tag: 'ul' | 'ol'; items: string[] } | null = null;
  let code: string[] | null = null;
  let paragraph: string[] = [];
  const inline = (text: string) => esc(text).replace(/`([^`]+)`/g, '<code>$1</code>').replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
  const flushParagraph = () => {
    if (paragraph.length) out += `<p>${inline(paragraph.join(' '))}</p>`;
    paragraph = [];
  };
  const flushList = () => {
    if (list) out += `<${list.tag}>${list.items.map((item) => `<li>${inline(item)}</li>`).join('')}</${list.tag}>`;
    list = null;
  };
  for (const line of source.split('\n')) {
    if (code !== null) {
      if (line.startsWith(fence)) {
        out += `<pre><code>${esc(code.join('\n'))}</code></pre>`;
        code = null;
      } else code.push(line);
      continue;
    }
    if (line.startsWith(fence)) {
      flushParagraph();
      flushList();
      code = [];
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      const level = (heading[1] as string).length;
      out += `<h${level}>${inline(heading[2] as string)}</h${level}>`;
      continue;
    }
    const bullet = /^\s*[-*]\s+(.*)/.exec(line) ?? /^\s*\d+\.\s+(.*)/.exec(line);
    if (bullet) {
      flushParagraph();
      const tag = /^\s*\d/.test(line) ? 'ol' : 'ul';
      if (!list || list.tag !== tag) {
        flushList();
        list = { tag, items: [] };
      }
      list.items.push(bullet[1] as string);
      continue;
    }
    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }
    flushList();
    paragraph.push(line.trim());
  }
  if (code !== null) out += `<pre><code>${esc(code.join('\n'))}</code></pre>`;
  flushParagraph();
  flushList();
  return out;
}

const DEFAULT_ICON: Record<EntryKind, string> = {
  skill: '<svg width="55%" height="55%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M12 3l2.2 5.3L20 9l-4.4 3.8L17 18.5 12 15.6 7 18.5l1.4-5.7L4 9l5.8-.7z"/></svg>',
  plugin: '<svg width="55%" height="55%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M9 3h6v3a2 2 0 104 0V9h2v6h-3a2 2 0 100 4h3v2H9v-3a2 2 0 10-4 0v3H3V9h3a2 2 0 100-4H3V3z"/></svg>',
  prompt: '<svg width="55%" height="55%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 5h16v11H9l-5 4z"/><path d="M8 9h8M8 12h5"/></svg>',
};

export function iconHtml(icon: IconView | null, kind: EntryKind, size: '' | 'lg' | 'sm' = ''): string {
  const cls = `icon ${size}`.trim();
  if (icon?.kind === 'image') return `<div class="${cls}"><img src="${esc(icon.url)}" alt="" loading="lazy"></div>`;
  if (icon?.kind === 'preset') return `<div class="${cls}" style="background:${esc(icon.color)}">${esc(icon.glyph)}</div>`;
  return `<div class="${cls}">${DEFAULT_ICON[kind]}</div>`;
}

export function toast(message: string): void {
  const element = document.createElement('div');
  element.className = 'toast';
  element.textContent = message;
  must('#toast-root').appendChild(element);
  setTimeout(() => element.remove(), 3500);
}

export const openModal = (html: string): void => {
  must('#modal-root').innerHTML = `<div class="overlay" data-act="overlay"><div class="modal" role="dialog" aria-modal="true">${html}</div></div>`;
};
export const closeModal = (): void => {
  if (document.querySelector<HTMLElement>('.modal')?.dataset.busy === 'true') return;
  must('#modal-root').innerHTML = '';
};
export const modalOpen = (): boolean => must('#modal-root').innerHTML !== '';

export function confirmModal(options: { title: string; body: string; ok: string; danger?: boolean }, onOk: () => void): void {
  openModal(`<div class="modal-head"><h2>${esc(options.title)}</h2><button class="x" data-act="close" aria-label="关闭">×</button></div>
    <p class="modal-sub">${options.body}</p><div class="modal-actions"><button class="btn" data-act="close">取消</button>
    <button class="${options.danger ? 'btn-danger' : 'btn-primary'}" id="confirm-ok">${esc(options.ok)}</button></div>`);
  must('#confirm-ok').onclick = () => {
    closeModal();
    onOk();
  };
}

/** Clicks anywhere in the page are handled by name: an element says what it does in `data-act`. */
type Handler = (element: HTMLElement, event: Event) => void | Promise<void>;
const handlers = new Map<string, Handler>();
export const onAct = (name: string, handler: Handler): void => void handlers.set(name, handler);
export function listenForActions(): void {
  document.addEventListener('click', (event) => {
    const target = event.target as Element | null;
    const element = target?.closest<HTMLElement>('[data-act]');
    if (!element) return;
    const name = element.dataset.act as string;
    if (name === 'overlay') {
      if (target === element) closeModal();
      return;
    }
    const handler = handlers.get(name);
    if (handler) {
      event.preventDefault();
      void handler(element, event);
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeModal();
  });
}
onAct('close', () => closeModal());

export const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const REFRESH = 'market:refresh';
/** Asks the page to load itself again. */
export const refresh = (): void => void window.dispatchEvent(new Event(REFRESH));

export const loadingBlock = (text = '正在加载…'): string => `<div class="empty"><span class="spin"></span> ${esc(text)}</div>`;
export const errorBlock = (message: string): string =>
  `<div class="empty"><b>暂时无法加载</b>${esc(message)}<br><br><button class="btn" data-act="reload">重试</button></div>`;
onAct('reload', () => refresh());

let pollTimer: number | undefined;
/** Asks again every so often while the page is on screen; going to another page stops it. */
export function startPoll(work: () => Promise<void>, ms: number): void {
  stopPoll();
  const tick = () => {
    pollTimer = window.setTimeout(async () => {
      if (!document.hidden && !modalOpen()) await work().catch(() => undefined);
      tick();
    }, ms);
  };
  tick();
}
export function stopPoll(): void {
  window.clearTimeout(pollTimer);
}
